import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { PlusIcon, Text, UnstyledButton } from '@/shared/ui';
import { formatLocalizedDate, formatLocalizedTime } from '@/shared/utils/date';
import type { Visit } from '@/services/requests/housien-models/Visit';
import { SectionCard } from '../SectionCard';

interface VisitsTabProps {
  visits: Visit[];
  loading: boolean;
  error: string | null;
  /**
   * Opens the schedule-visit drawer, which the detail view owns so the
   * header button, the phone action bar and this tab all raise the same one.
   */
  onSchedule?: () => void;
}

/*
 * Keyed on the statuses a visit actually carries -- pending, confirmed,
 * done, cancelled, no_show -- which is what `Visit` declares and what the
 * visits screen writes.
 *
 * This map used to key on `scheduled` and `completed`, which no visit has
 * ever had. Every pending, confirmed and done visit missed the lookup and
 * fell through to the scheduled fallback, so a visit that had already
 * happened was announced here as still being scheduled.
 */
const VISIT_STATUS_CONFIG: Record<string, { labelKey: string; className: string }> = {
  pending: { labelKey: 'detailView.visits.scheduled', className: 'bg-[color-mix(in_srgb,var(--seed-base)_8%,var(--surface-primary))] text-[var(--seed-base)]' },
  confirmed: { labelKey: 'detailView.visits.confirmed', className: 'bg-[color-mix(in_srgb,var(--status-info)_10%,var(--surface-primary))] text-[var(--status-info-text)]' },
  done: { labelKey: 'detailView.visits.completed', className: 'bg-[color-mix(in_srgb,var(--status-success)_10%,var(--surface-primary))] text-[var(--status-success-text)]' },
  cancelled: { labelKey: 'detailView.visits.cancelled', className: 'bg-[color-mix(in_srgb,var(--status-error)_10%,var(--surface-primary))] text-[var(--status-error-text)]' },
  no_show: { labelKey: 'detailView.visits.noShow', className: 'bg-[color-mix(in_srgb,var(--status-warning)_10%,var(--surface-primary))] text-[var(--accent-mostarda)]' },
};

/**
 * Formats an ISO visit datetime into a localized date and time pair,
 * returning a dash placeholder when the datetime is missing or invalid.
 */
function formatVisitDate(datetime?: string): { date: string; time: string } {
  if (!datetime) return { date: '—', time: '' };
  try {
    return {
      date: formatLocalizedDate(datetime) || '—',
      time: formatLocalizedTime(datetime) || '',
    };
  } catch {
    return { date: datetime, time: '' };
  }
}

/**
 * Renders a single visit row: client name, date/time, broker, and status badge.
 */
const VisitRow: React.FC<{ visit: Visit; t: TFunction }> = ({ visit, t }) => {
  const { date, time } = formatVisitDate(visit.visit_datetime);
  const statusCfg = VISIT_STATUS_CONFIG[visit.status ?? ''] ?? VISIT_STATUS_CONFIG.pending;

  return (
    <div className="flex justify-between items-center px-[var(--space-3)] py-[var(--space-2)] rounded-(--radius-md) bg-[var(--surface-primary)] border border-[var(--border-subtle)]">
      <div>
        <div className="text-[length:var(--text-body-sm)] [font-weight:var(--font-medium)] text-[var(--text-primary)]">
          {visit.client_name || t('detailView.visits.unknownClient')}
        </div>
        <div className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]">
          {date}{time && ` ${t('detailView.visits.atTime')} ${time}`}
          {visit.broker_name && ` · ${visit.broker_name}`}
        </div>
      </div>
      <Text as="span" className={`text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] px-[var(--space-2)] py-[var(--space-1)] rounded-(--radius-sm) whitespace-nowrap ${statusCfg.className}`}>
        {t(statusCfg.labelKey)}
      </Text>
    </div>
  );
};

/**
 * Renders the visits tab for a property: a loading skeleton, an error notice,
 * an empty state, or the list of scheduled/completed visits with status badges.
 */
export const VisitsTab: React.FC<VisitsTabProps> = ({
  visits,
  loading,
  error,
  onSchedule,
}) => {
  const { t } = useTranslation('features/properties');

  const scheduleButton = onSchedule ? (
    <ScheduleVisitButton
      label={t('detailView.visits.scheduleVisit')}
      onClick={onSchedule}
    />
  ) : null;

  if (loading) {
    return (
      <SectionCard title={t('detailView.visits.title')}>
        <div className="flex flex-col gap-[var(--space-3)]">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-[52px] rounded-(--radius-md) bg-[var(--surface-tertiary)] animate-pulse" />
          ))}
        </div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title={t('detailView.visits.title')}>
        <Text className="text-[length:var(--text-body-sm)] text-[var(--status-error)] m-0">
          {t('detailView.visits.errorLoading')} {error}
        </Text>
      </SectionCard>
    );
  }

  return (
    <>
      <SectionCard title={t('detailView.visits.title')} titleAccessory={scheduleButton}>
        {visits.length === 0 ? (
          <div className="flex items-center justify-center h-[120px] text-[var(--text-tertiary)] text-[length:var(--text-body-sm)]">
            {t('detailView.visits.noVisits')}
          </div>
        ) : (
          <div className="flex flex-col gap-[var(--space-3)]">
            {visits.map((visit) => (
              <VisitRow key={visit.visit_id} visit={visit} t={t} />
            ))}
          </div>
        )}
      </SectionCard>
    </>
  );
};

/**
 * Call-to-action in the card header that opens the schedule-visit drawer
 * prefilled with the current property.
 *
 * @param props - The button label and click handler.
 * @returns The rendered schedule-visit button.
 */
const ScheduleVisitButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <UnstyledButton
    type="button"
    data-testid="property-schedule-visit"
    onClick={onClick}
    aria-label={label}
    className="flex h-[34px] items-center gap-(--space-1-5) whitespace-nowrap rounded-(--radius-md) border border-solid border-[var(--border-medium)] bg-[var(--surface-primary)] px-(--space-3) text-[var(--text-primary)] hover:bg-[var(--surface-hover)]"
  >
    <PlusIcon width={14} height={14} />
    <Text as="span" className="text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]">{label}</Text>
  </UnstyledButton>
);
